import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, X, ArrowRight, AlertCircle, HelpCircle } from "lucide-react";

const CHANNELS = [
  { value: "auto", label: "Auto-detect" },
  { value: "shopify", label: "Shopify" },
  { value: "amazon", label: "Amazon" },
  { value: "flipkart", label: "Flipkart" },
  { value: "offline", label: "Offline / B2B" },
  { value: "other", label: "Other" },
];

const EMPTY_NEW_SKU = { product_name: "", category: "Imported", mrp: 0, cogs: 0, packaging_cost: 0, gst_percent: 18 };

export default function CSVUploadDialog({ open, onOpenChange, onCommitted }) {
  const { current } = useWorkspace();
  const [files, setFiles] = useState([]);
  const [channel, setChannel] = useState("auto");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { upload_id, unmatched_skus, master_skus, channels_summary, ... }
  const [masterSkus, setMasterSkus] = useState([]);
  const [mappings, setMappings] = useState({}); // {alias: master_sku_id or "__CREATE__"}
  const [newSkus, setNewSkus] = useState({}); // {alias: {name, cogs, ...}}
  const [committing, setCommitting] = useState(false);
  const [headersOpen, setHeadersOpen] = useState(false);
  const [expected, setExpected] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFiles([]); setChannel("auto"); setPreview(null); setMappings({}); setNewSkus({});
    if (current) {
      api.get("/skus", { params: { workspace_id: current.id } }).then((r) => setMasterSkus(r.data));
    }
    if (!expected) {
      api.get("/orders/upload/expected-headers").then((r) => setExpected(r.data)).catch(() => {});
    }
  }, [open, current]); // eslint-disable-line

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles([...files, ...list]);
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const upload = async () => {
    if (!current || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post("/orders/upload", fd, {
        params: { workspace_id: current.id, channel },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      // Pre-fill mappings if no unmatched
      if (!data.needs_mapping && data.row_count > 0) {
        // Auto-commit
        await commit(data.upload_id, {}, {});
      } else if (data.row_count === 0) {
        toast.error("0 rows parsed — check the missing headers listed below");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const commit = async (upload_id, m, n) => {
    setCommitting(true);
    try {
      const { data } = await api.post("/orders/upload/commit", {
        upload_id: upload_id || preview.upload_id,
        mappings: m || mappings,
        new_skus: n || newSkus,
      });
      toast.success(`Imported ${data.inserted} orders across ${data.affected_dates.length} day(s)`);
      onCommitted?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  const canCommit = useMemo(() => {
    if (!preview) return false;
    for (const s of preview.unmatched_skus) {
      const m = mappings[s];
      if (!m) return false;
      if (m === "__CREATE__") {
        const n = newSkus[s];
        if (!n?.product_name || !n?.mrp || !n?.cogs) return false;
      }
    }
    return true;
  }, [preview, mappings, newSkus]);

  const setMapping = (alias, val) => {
    setMappings({ ...mappings, [alias]: val });
    if (val === "__CREATE__" && !newSkus[alias]) {
      setNewSkus({ ...newSkus, [alias]: { ...EMPTY_NEW_SKU, product_name: alias } });
    }
  };
  const setNewField = (alias, key, val) => {
    setNewSkus({ ...newSkus, [alias]: { ...(newSkus[alias] || EMPTY_NEW_SKU), [key]: val } });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="csv-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {preview?.needs_mapping ? "Map Unrecognized SKUs" : "Import Orders from CSV"}
          </DialogTitle>
        </DialogHeader>

        {!preview && (
          <div className="space-y-6">
            <div>
              <Label className="overline">Channel</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="rounded-none mt-2 h-11" data-testid="upload-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">Auto-detect scans headers to identify Shopify / Amazon / Flipkart. Pick a channel to force parsing.</p>
              <button
                type="button"
                onClick={() => setHeadersOpen((v) => !v)}
                className="mt-2 inline-flex items-center gap-1 text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground"
                data-testid="view-expected-headers-link"
              >
                <HelpCircle className="h-3 w-3" /> {headersOpen ? "Hide" : "View"} expected headers
              </button>
              {headersOpen && expected && (
                <div className="mt-3 border border-border" data-testid="expected-headers-table">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="overline">Logical Field</TableHead>
                        {["shopify","amazon","flipkart","offline","other"].map((ch) => (
                          <TableHead key={ch} className="overline text-xs capitalize">{ch}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(expected.labels).map(([key, label]) => (
                        <TableRow key={key}>
                          <TableCell className="text-xs font-medium">{label}</TableCell>
                          {["shopify","amazon","flipkart","offline","other"].map((ch) => (
                            <TableCell key={ch} className="text-[10px] font-mono text-muted-foreground">
                              {(expected.channels[ch]?.[key] || []).slice(0, 4).join(" · ") || "—"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="p-2 text-[10px] text-muted-foreground border-t border-border">
                    Headers are normalized case-/space-/hyphen-/underscore-insensitive, so <span className="font-mono">Order Date</span>, <span className="font-mono">order_date</span>, and <span className="font-mono">order-date</span> all match.
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="overline">CSV Files</Label>
              <label
                htmlFor="csv-file-input"
                className="mt-2 border-2 border-dashed border-border p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-accent transition-colors"
                data-testid="csv-dropzone"
              >
                <UploadCloud className="h-8 w-8 text-muted-foreground mb-2" />
                <div className="text-sm">Click to add CSV files (or drag &amp; drop)</div>
                <div className="text-xs text-muted-foreground mt-1">Multiple files supported · Shopify, Amazon, Flipkart, Offline</div>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv,text/csv"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                  data-testid="csv-file-input"
                />
              </label>
              {files.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="csv-file-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between border border-border px-3 py-2">
                      <div className="text-sm font-mono truncate">{f.name}</div>
                      <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button onClick={upload} disabled={uploading || files.length === 0} className="rounded-none" data-testid="csv-upload-btn">
                {uploading ? "Parsing…" : `Parse ${files.length} file${files.length === 1 ? "" : "s"}`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {preview?.needs_mapping && (
          <div className="space-y-5">
            <div className="border border-border p-4 bg-accent/40 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[hsl(var(--negative))] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium">Found {preview.unmatched_skus.length} unrecognized SKU(s) across {preview.row_count} rows.</div>
                <div className="text-xs text-muted-foreground mt-1">Map each to an existing Master SKU or create a new one. Mappings save automatically for future uploads.</div>
              </div>
            </div>

            <div className="space-y-4" data-testid="mapping-list">
              {preview.unmatched_skus.map((alias) => {
                const mapped = mappings[alias];
                return (
                  <div key={alias} className="border border-border p-4" data-testid={`map-row-${alias}`}>
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-sm px-2 py-1 border border-border">{alias}</div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <Select value={mapped || ""} onValueChange={(v) => setMapping(alias, v)}>
                        <SelectTrigger className="rounded-none flex-1" data-testid={`map-select-${alias}`}><SelectValue placeholder="Map to Master SKU…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__CREATE__" data-testid={`create-new-${alias}`}>➕ Create new Master SKU</SelectItem>
                          {masterSkus.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.sku_code} · {s.product_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {mapped === "__CREATE__" && (
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`new-sku-form-${alias}`}>
                        <div className="col-span-2">
                          <Label className="overline">Product Name</Label>
                          <Input value={newSkus[alias]?.product_name || ""} onChange={(e) => setNewField(alias, "product_name", e.target.value)} className="rounded-none mt-1" />
                        </div>
                        <div>
                          <Label className="overline">Category</Label>
                          <Input value={newSkus[alias]?.category || "Imported"} onChange={(e) => setNewField(alias, "category", e.target.value)} className="rounded-none mt-1" />
                        </div>
                        <div>
                          <Label className="overline">MRP ₹</Label>
                          <Input type="number" step="0.01" value={newSkus[alias]?.mrp || 0} onChange={(e) => setNewField(alias, "mrp", e.target.value)} className="rounded-none mt-1 font-mono" />
                        </div>
                        <div>
                          <Label className="overline">COGS ₹</Label>
                          <Input type="number" step="0.01" value={newSkus[alias]?.cogs || 0} onChange={(e) => setNewField(alias, "cogs", e.target.value)} className="rounded-none mt-1 font-mono" />
                        </div>
                        <div>
                          <Label className="overline">Packaging ₹</Label>
                          <Input type="number" step="0.01" value={newSkus[alias]?.packaging_cost || 0} onChange={(e) => setNewField(alias, "packaging_cost", e.target.value)} className="rounded-none mt-1 font-mono" />
                        </div>
                        <div>
                          <Label className="overline">GST %</Label>
                          <Input type="number" step="0.01" value={newSkus[alias]?.gst_percent || 18} onChange={(e) => setNewField(alias, "gst_percent", e.target.value)} className="rounded-none mt-1 font-mono" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="border border-border p-4">
              <div className="overline text-muted-foreground mb-2">Import Preview</div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="overline">Channel</TableHead>
                    <TableHead className="overline text-right">Orders</TableHead>
                    <TableHead className="overline text-right">Units</TableHead>
                    <TableHead className="overline text-right">Net Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.channels_summary.map((c) => (
                    <TableRow key={c.channel}>
                      <TableCell className="font-mono uppercase text-xs">{c.channel}</TableCell>
                      <TableCell className="text-right font-mono">{c.orders}</TableCell>
                      <TableCell className="text-right font-mono">{c.units}</TableCell>
                      <TableCell className="text-right font-mono">{inr(c.revenue, { decimals: 0 })}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="text-xs text-muted-foreground mt-3 font-mono">
                Date range · {preview.date_range.from} → {preview.date_range.to}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button
                onClick={() => commit()}
                disabled={!canCommit || committing}
                className="rounded-none"
                data-testid="csv-commit-btn"
              >
                {committing ? "Importing…" : `Commit ${preview.row_count} rows`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
